import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - The slide elements.
 * @property {HTMLButtonElement[]} dots - The pagination dot buttons.
 * @property {HTMLElement[]} comparisons - The before-after comparison elements.
 * @property {HTMLElement[]} dividers - The divider handle elements.
 * @property {HTMLButtonElement} prevButton - The previous arrow button.
 * @property {HTMLButtonElement} nextButton - The next arrow button.
 */

/**
 * A slider component with before/after image comparison functionality.
 * Uses fade transitions between slides and custom drag logic for the comparison.
 *
 * @extends Component<Refs>
 */
class BeforeAfterSlider extends Component {
  /** @type {number} */
  #currentIndex = 0;

  /** @type {HTMLElement | null} */
  #activeComparison = null;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {EventListener | null} */
  #boundHandleMove = null;

  /** @type {EventListener | null} */
  #boundHandleEnd = null;

  /** @type {EventListener | null} */
  #boundHandleDividerStart = null;

  /** @type {EventListener | null} */
  #boundHandleBlockSelect = null;

  /** @type {boolean} */
  #isVertical = false;

  connectedCallback() {
    super.connectedCallback();
    this.#isVertical = this.dataset.layoutMode === 'vertical';
    this.#initSlides();
    this.#initComparisons();
    this.#initEditorEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeComparisonListeners();
    this.#removeEditorEvents();
  }

  /**
   * Initialize slides - set first slide as active
   */
  #initSlides() {
    const { slides } = this.refs;
    if (!slides || slides.length === 0) return;

    const firstSlide = slides[0];
    if (!firstSlide) return;
    firstSlide.classList.add('is-active');
  }

  /**
   * Handle dot click - switch to selected slide
   * @param {number} index - The slide index to go to
   */
  handleDotClick(index) {
    if (index === this.#currentIndex) return;

    const { slides, dots } = this.refs;
    if (!slides || !dots) return;

    // Update slides
    slides[this.#currentIndex]?.classList.remove('is-active');
    slides[index]?.classList.add('is-active');

    // Update dots
    dots[this.#currentIndex]?.classList.remove('before-after-slider__dot--active');
    dots[index]?.classList.add('before-after-slider__dot--active');

    this.#currentIndex = index;
  }

  /**
   * Go to a specific slide index
   * @param {number} index - The slide index to go to
   */
  #goToSlide(index) {
    const { slides } = this.refs;
    if (!slides || index < 0 || index >= slides.length) return;

    // Update slides
    slides[this.#currentIndex]?.classList.remove('is-active');
    slides[index]?.classList.add('is-active');

    this.#currentIndex = index;
    this.#updateArrowStates();
  }

  /**
   * Handle previous arrow click
   */
  handlePrevClick() {
    if (this.#currentIndex > 0) {
      this.#goToSlide(this.#currentIndex - 1);
    }
  }

  /**
   * Handle next arrow click
   */
  handleNextClick() {
    const { slides } = this.refs;
    if (slides && this.#currentIndex < slides.length - 1) {
      this.#goToSlide(this.#currentIndex + 1);
    }
  }

  /**
   * Update arrow button disabled states
   */
  #updateArrowStates() {
    const { slides, prevButton, nextButton } = this.refs;
    if (!slides || !prevButton || !nextButton) return;

    prevButton.disabled = this.#currentIndex === 0;
    nextButton.disabled = this.#currentIndex === slides.length - 1;
  }

  /**
   * Initialize all before/after comparisons
   */
  #initComparisons() {
    // Skip divider setup for vertical mode
    if (this.#isVertical) return;

    const { comparisons, dividers } = this.refs;
    if (!comparisons) return;

    // Set initial position
    comparisons.forEach((comparison) => {
      this.#setComparisonPosition(comparison, 50);
    });

    // Bind event handlers
    const handleMove = /** @type {EventListener} */ ((event) =>
      this.#handleMove(/** @type {MouseEvent | TouchEvent} */ (event)));
    const handleEnd = /** @type {EventListener} */ ((event) =>
      this.#handleEnd(/** @type {MouseEvent | TouchEvent} */ (event)));
    const handleDividerStart = /** @type {EventListener} */ ((event) =>
      this.#handleDividerStart(/** @type {MouseEvent | TouchEvent} */ (event)));

    this.#boundHandleMove = handleMove;
    this.#boundHandleEnd = handleEnd;
    this.#boundHandleDividerStart = handleDividerStart;

    // Add mousedown/touchstart listeners to dividers
    if (dividers) {
      dividers.forEach((divider) => {
        divider.addEventListener('mousedown', handleDividerStart);
        divider.addEventListener('touchstart', handleDividerStart);
      });
    }

    // Add global mouse/touch listeners for dragging
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }

  /**
   * Remove comparison event listeners
   */
  #removeComparisonListeners() {
    const { dividers } = this.refs;
    const handleDividerStart = this.#boundHandleDividerStart;
    const handleMove = this.#boundHandleMove;
    const handleEnd = this.#boundHandleEnd;

    // Remove divider listeners
    if (dividers && handleDividerStart) {
      dividers.forEach((divider) => {
        divider.removeEventListener('mousedown', handleDividerStart);
        divider.removeEventListener('touchstart', handleDividerStart);
      });
    }

    // Remove global listeners
    if (handleMove) {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
    }
    if (handleEnd) {
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchend', handleEnd);
    }
  }

  /**
   * Handle divider drag start
   * @param {MouseEvent | TouchEvent} event
   */
  #handleDividerStart(event) {
    event.preventDefault();

    const target = /** @type {HTMLElement} */ (event.currentTarget);
    const comparison = target.closest('.ba-slide__comparison');

    if (comparison instanceof HTMLElement) {
      this.#isDragging = true;
      this.#activeComparison = comparison;
      this.#activeComparison.classList.add('ba-slide__comparison--dragging');
    }
  }

  /**
   * Handle mouse/touch move
   * @param {MouseEvent | TouchEvent} event
   */
  #handleMove(event) {
    if (!this.#isDragging || !this.#activeComparison) return;

    event.preventDefault();

    let clientX = 0;
    if ('touches' in event) {
      const touch = event.touches[0];
      if (!touch) return;
      clientX = touch.clientX;
    } else {
      clientX = event.clientX;
    }
    const rect = this.#activeComparison.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    const clampedPosition = Math.max(0, Math.min(100, position));

    this.#setComparisonPosition(this.#activeComparison, clampedPosition);
  }

  /**
   * Handle mouse/touch end
   * @param {MouseEvent | TouchEvent} _event
   */
  #handleEnd(_event) {
    if (!this.#isDragging) return;

    if (this.#activeComparison) {
      this.#activeComparison.classList.remove('ba-slide__comparison--dragging');
    }

    this.#isDragging = false;
    this.#activeComparison = null;
  }

  /**
   * Set the comparison position
   * @param {HTMLElement} comparison - The comparison element
   * @param {number} position - The position percentage (0-100)
   */
  #setComparisonPosition(comparison, position) {
    comparison.style.setProperty('--divider-position', `${position}%`);
  }

  /**
   * Initialize Shopify theme editor events
   */
  #initEditorEvents() {
    if (!window.Shopify?.designMode) return;

    this.#boundHandleBlockSelect = (event) => this.#handleBlockSelect(event);
    this.addEventListener('shopify:block:select', this.#boundHandleBlockSelect);
  }

  /**
   * Remove Shopify theme editor events
   */
  #removeEditorEvents() {
    if (this.#boundHandleBlockSelect) {
      this.removeEventListener('shopify:block:select', this.#boundHandleBlockSelect);
    }
  }

  /**
   * Handle block selection in theme editor
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    const { slides, dots } = this.refs;
    if (!slides) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const selectedSlide = target.closest('.ba-slide');
    if (!(selectedSlide instanceof HTMLElement)) return;

    const index = slides.indexOf(selectedSlide);
    if (index !== -1 && index !== this.#currentIndex) {
      // Update slides
      slides[this.#currentIndex]?.classList.remove('is-active');
      slides[index]?.classList.add('is-active');

      // Update dots if they exist (pagination mode)
      if (dots) {
        dots[this.#currentIndex]?.classList.remove('before-after-slider__dot--active');
        dots[index]?.classList.add('before-after-slider__dot--active');
      }

      this.#currentIndex = index;
      this.#updateArrowStates();
    }
  }
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}
